import React from 'react';
import { Link } from 'react-router-dom';
import { Palette, Check, ArrowRight, Pen, Image, FileText, Zap, Eye, Heart } from 'lucide-react';
import { mockData } from '../mock';
import SEO from '../components/SEO';

const DesignServicesPage = () => {
  const { services } = mockData;
  const designServices = services.design;

  const designPrinciples = [
    { icon: Eye, title: 'Görsel İmpakt', description: 'İlk bakışta etki yaratan tasarımlar' },
    { icon: Heart, title: 'Duygusal Bağ', description: 'Hedef kitlenizle duygusal bağ kurun' },
    { icon: Zap, title: 'Yaratıcı Çözümler', description: 'Sektörde öne çıkan yaratıcı fikirler' },
    { icon: Pen, title: 'El İşçiliği', description: 'Her detayda özenli işçilik' }
  ];

  const designProcess = [
    { step: 1, title: 'Briefing & Araştırma', description: 'Markanızı ve hedeflerinizi anlıyoruz.' },
    { step: 2, title: 'Konsept Geliştirme', description: '3 farklı tasarım konsepti sunuyoruz.' },
    { step: 3, title: 'Revizyon & Düzeltme', description: 'Geri bildirimlerinizle mükemmelleştiriyoruz.' },
    { step: 4, title: 'Final & Teslim', description: 'Tüm formatlar ve dosyaları teslim ediyoruz.' }
  ];

  const portfolioShowcase = [
    {
      title: 'Modern Logo Tasarımları',
      image: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=400&h=300&fit=crop&crop=center',
      category: 'Logo Design'
    },
    {
      title: 'Kurumsal Kimlik Setleri',
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=300&fit=crop&crop=center',
      category: 'Brand Identity'
    },
    {
      title: 'Broşür ve Katalog Tasarımları',
      image: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=400&h=300&fit=crop&crop=center',
      category: 'Print Design'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <SEO 
        title="Logo Tasarımı & Kurumsal Kimlik"
        description="Viral Strateji ile markanıza değer katan tasarımlar oluşturun. Logo tasarımı, kurumsal kimlik ve dijital varlık tasarımı hizmetlerimiz."
        keywords="logo tasarımı, kurumsal kimlik tasarımı, marka kimliği, grafik tasarım, website tasarımı"
      />
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">

            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              {designServices.title}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {designServices.subtitle}
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white px-6 py-2.5 rounded-full font-semibold text-base hover:from-pink-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>Tasarım Projesi Başlat</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {designServices.items.map((service, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-pink-500 to-purple-600" />
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0 mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                    <span className="bg-gradient-to-r from-pink-100 to-purple-100 text-pink-800 px-4 py-2 rounded-xl font-semibold text-sm self-start sm:self-auto">
                      {service.price}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/iletisim"
                    className="block w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-2.5 px-6 rounded-full font-semibold text-sm text-center hover:from-pink-700 hover:to-purple-700 transition-all duration-300 transform group-hover:scale-105"
                  >
                    Teklif Al
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Principles */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-pink-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tasarım Yaklaşımımız
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Her projede uyguladığımız temel tasarım prensipleri.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {designPrinciples.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="bg-gradient-to-br from-pink-500 to-purple-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{principle.title}</h3>
                  <p className="text-gray-600">{principle.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Örnek Çalışmalarımız
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Farklı sektörlerde gerçekleştirdiğimiz başarılı tasarım projeleri.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {portfolioShowcase.map((item, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
                <div className="aspect-w-16 aspect-h-12 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {item.category}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/portfolyo"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white px-6 py-2.5 rounded-full font-semibold text-base hover:from-pink-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              <span>Tüm Çalışmaları Görüntüle</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-pink-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tasarım Süreci
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Mükemmel sonuçlar için uygulanan sistematik sürecimiz.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-8">
              {designProcess.map((phase, index) => (
                <div key={index} className="flex items-center space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl">
                    {phase.step}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-gray-900">{phase.title}</h3>
                    <p className="text-gray-600 mt-1">{phase.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-600 via-purple-600 to-blue-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Markanızı Güçlü Tasarımlarla Öne Çıkarın
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Profesyonel tasarım ekibimizle görüşerek markanıza değer katacak çözümler geliştirin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-white text-pink-600 px-6 py-2.5 rounded-full font-semibold text-base hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              <span>Tasarım Projesi Başlat</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/portfolyo"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-6 py-2.5 rounded-full font-semibold text-base hover:bg-white hover:text-pink-600 transition-all duration-300 transform hover:scale-105"
            >
              <span>Portfolyo Görüntüle</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignServicesPage;